import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import { getComicBySlug, getRelatedComics, getCategoryBySlug } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = getComicBySlug(slug || '');
  const relatedComics = comic ? getRelatedComics(comic) : [];
  const category = comic ? getCategoryBySlug(comic.category) : undefined;

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-error-warning-line text-foreground-300 text-6xl w-16 h-16 flex items-center justify-center mx-auto mb-4"></i>
            <h2 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">该漫画不存在或已被移除</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫网`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.updateDate || '',
    dateModified: comic.updateDate,
    url: `https://www.hanmanwang.com/comic/${slug}`,
    publisher: {
      '@type': 'Organization',
      name: '韩漫网',
    },
  };

  const comicJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    url: `https://www.hanmanwang.com/comic/${slug}`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: '5',
      ratingCount: Math.floor(comic.views / 1000),
    },
    numberOfPages: comic.totalChapters,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <SEOMeta
        title={`${comic.title} - 韩漫在线免费阅读`}
        description={`${comic.description.substring(0, 150)} 作者:${comic.author} | 状态:${comic.status} | 总话数:${comic.totalChapters}话。韩漫网提供${comic.title}下拉式免费在线阅读。`}
        keywords={`${comic.title},${comic.title}韩漫,${comic.category}韩漫,韩漫在线阅读,韩漫网`}
        canonicalUrl={`https://www.hanmanwang.com/comic/${slug}`}
        ogType="article"
        ogImage={comic.coverUrl}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(comicJsonLd) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${category?.name || '漫画'}`, href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />
          </div>

          <article className="max-w-7xl mx-auto px-4 md:px-6 pb-10">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-72 flex-shrink-0">
                <div className="rounded-lg overflow-hidden bg-background-100 aspect-[2/3] lg:sticky lg:top-24">
                  <img
                    src={comic.coverUrl}
                    alt={`${comic.title} - 韩漫在线免费阅读`}
                    title={`${comic.title} - 韩漫网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">{comic.title}</h1>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  {comic.status === '已完结' ? (
                    <span className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">
                      {comic.status}
                    </span>
                  ) : (
                    <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">
                      {comic.status}
                    </span>
                  )}
                  <span className="text-sm text-foreground-500">作者：{comic.author}</span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-star-fill text-accent-500 w-4 h-4 inline-flex items-center justify-center"></i>{' '}
                    {comic.rating}
                  </span>
                  <span className="text-sm text-foreground-500">
                    共{comic.totalChapters}话
                  </span>
                  <span className="text-sm text-foreground-500">
                    {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}次阅读
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="prose prose-sm max-w-none mb-8">
                  <h2 className="font-heading text-xl text-foreground-900 mb-3">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-xl text-foreground-900">章节列表</h2>
                    <span className="text-xs text-foreground-500">共{comic.totalChapters}话</span>
                  </div>
                  <div className="border border-background-200 rounded-lg divide-y divide-background-200 max-h-96 overflow-y-auto">
                    {comic.chapters.map((chapter) => (
                      <div
                        key={chapter.id}
                        className="flex items-center justify-between px-4 py-3 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-foreground-400 w-8 flex-shrink-0">#{chapter.id}</span>
                          <span className="text-sm text-foreground-800">{chapter.title}</span>
                        </div>
                        <span className="text-xs text-foreground-400 flex-shrink-0">{chapter.updateDate}</span>
                      </div>
                    ))}
                  </div>
                  {comic.totalChapters > 4 && (
                    <p className="mt-2 text-xs text-foreground-400 text-center">
                      以上为最近更新的章节，更多章节即将上线
                    </p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    开始阅读第一话
                  </button>
                  <button className="px-8 py-3 border border-background-300 text-foreground-700 rounded-full font-medium text-sm hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                    加入收藏
                  </button>
                </div>
              </div>
            </div>

            {relatedComics.length > 0 && (
              <section className="mt-14">
                <h2 className="font-heading text-2xl text-foreground-950 mb-6">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}